import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ClipboardCheck, Loader2, MessageSquare, X, Star } from 'lucide-react';
import toast from 'react-hot-toast';
import { Badge } from '@/components/common/Badge';
import { cn } from '@/utils/cn';
import { trainingService } from '@/services/training.service';
import { useTrainingStore } from '@/store/trainingStore';
import type { TrainingReview } from '@/types';

type FilterStatus = '' | 'pending' | 'approved' | 'rejected';

const filterOptions: { label: string; value: FilterStatus }[] = [
  { label: 'All', value: '' },
  { label: 'Pending', value: 'pending' },
  { label: 'Approved', value: 'approved' },
  { label: 'Rejected', value: 'rejected' },
];

export const TrainingReviewsPage: React.FC = () => {
  const { id: reviewIdFromUrl } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { reviews, reviewsLoading, fetchReviews, submitVerdict } = useTrainingStore();

  const [filterStatus, setFilterStatus] = useState<FilterStatus>('');
  const [selectedReview, setSelectedReview] = useState<TrainingReview | null>(null);

  // Verdict form
  const [verdictStatus, setVerdictStatus] = useState<'approved' | 'rejected'>('approved');
  const [verdictScore, setVerdictScore] = useState<number>(3);
  const [verdictFeedback, setVerdictFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [conversationMessages, setConversationMessages] = useState<Array<{ id: string; sender_type: string; content: string; created_at: string }>>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  useEffect(() => {
    fetchReviews(filterStatus || undefined).catch(() =>
      toast.error('Failed to load reviews')
    );
  }, [filterStatus, fetchReviews]);

  // Open review from URL param on initial load
  useEffect(() => {
    if (reviewIdFromUrl && reviews.length > 0 && !selectedReview) {
      const review = reviews.find((r) => r.id === reviewIdFromUrl);
      if (review) {
        openReviewModal(review);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewIdFromUrl, reviews]);

  const openReviewModal = (review: TrainingReview) => {
    setSelectedReview(review);
    navigate('/training/reviews/' + review.id);
    setVerdictStatus(review.status === 'rejected' ? 'rejected' : 'approved');
    setVerdictScore(review.score ?? 3);
    setVerdictFeedback(review.feedback ?? '');
    // Load conversation messages
    setMessagesLoading(true);
    setConversationMessages([]);
    trainingService.getSessionMessages(review.session_id)
      .then((messages) => setConversationMessages(messages))
      .catch(() => {})
      .finally(() => setMessagesLoading(false));
  };

  const closeReviewModal = () => {
    setSelectedReview(null);
    navigate('/training/reviews');
    setVerdictStatus('approved');
    setVerdictScore(3);
    setVerdictFeedback('');
    setConversationMessages([]);
  };

  const handleSubmitVerdict = async () => {
    if (!selectedReview) return;

    setIsSubmitting(true);
    try {
      await submitVerdict(selectedReview.id, {
        status: verdictStatus,
        score: verdictScore,
        feedback: verdictFeedback.trim(),
      });
      toast.success('Verdict submitted');
      closeReviewModal();
      fetchReviews(filterStatus || undefined).catch(() => {});
    } catch {
      toast.error('Failed to submit verdict');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getReviewStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Training Reviews</h1>
        <p className="text-gray-500 mt-1">
          Review and evaluate training session performance
        </p>
      </div>

      {/* Filter Buttons */}
      <div className="flex items-center gap-2">
        {filterOptions.map((option) => (
          <button
            key={option.value}
            onClick={() => setFilterStatus(option.value)}
            className={cn(
              'px-4 py-2 rounded-full text-sm font-medium transition-colors',
              filterStatus === option.value
                ? 'bg-violet-500 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            )}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* Reviews List */}
      {reviewsLoading ? (
        <div className="bg-white rounded-xl border-2 border-[#1a1a2e] p-12 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-violet-600 animate-spin" />
        </div>
      ) : reviews.length === 0 ? (
        <div className="bg-white rounded-xl border-2 border-[#1a1a2e] p-12">
          <div className="text-center max-w-md mx-auto">
            <div className="w-16 h-16 rounded-xl bg-gray-50 flex items-center justify-center mx-auto mb-6">
              <ClipboardCheck className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No reviews found
            </h3>
            <p className="text-gray-500">
              {filterStatus
                ? `No ${filterStatus} reviews at the moment.`
                : 'Training session reviews will appear here once sessions are completed.'}
            </p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {reviews.map((review) => (
            <div
              key={review.id}
              onClick={() => openReviewModal(review)}
              className="bg-white rounded-xl border-2 border-[#1a1a2e] overflow-hidden hover:border-violet-600 hover:shadow-[5px_5px_0px_#1a1a2e] transition-all cursor-pointer"
            >
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-mono text-gray-500 truncate">
                      Session: {review.session_id.slice(0, 8)}...
                    </p>
                    {review.reviewer_name && (
                      <p className="text-sm font-medium text-gray-900 mt-1">
                        Reviewer: {review.reviewer_name}
                      </p>
                    )}
                  </div>
                  <Badge variant={getReviewStatusBadge(review.status)}>
                    {review.status}
                  </Badge>
                </div>

                {review.score !== null && (
                  <div className="flex items-center gap-1 mb-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={cn(
                          'w-4 h-4',
                          star <= (review.score ?? 0)
                            ? 'text-yellow-400 fill-yellow-400'
                            : 'text-gray-300'
                        )}
                      />
                    ))}
                    <span className="text-sm font-medium text-gray-700 ml-1">
                      {review.score}/5
                    </span>
                  </div>
                )}

                {review.feedback && (
                  <p className="text-sm text-gray-600 line-clamp-2">{review.feedback}</p>
                )}

                <p className="mt-3 text-xs text-gray-400">
                  {new Date(review.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Review Modal */}
      {selectedReview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={closeReviewModal} />
          <div className="relative bg-white rounded-xl p-6 max-w-3xl w-full max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-500 rounded-xl flex items-center justify-center">
                  <ClipboardCheck className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Review Session</h2>
              </div>
              <button
                onClick={closeReviewModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Status Info */}
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Session ID</p>
                  <p className="text-sm font-mono text-gray-900 truncate">
                    {selectedReview.session_id}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Current Status</p>
                  <div className="mt-0.5">
                    <Badge variant={getReviewStatusBadge(selectedReview.status)}>
                      {selectedReview.status}
                    </Badge>
                  </div>
                </div>
                {selectedReview.reviewer_name && (
                  <div>
                    <p className="text-xs text-gray-500">Reviewer</p>
                    <p className="text-sm text-gray-900">{selectedReview.reviewer_name}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-gray-500">Created</p>
                  <p className="text-sm text-gray-900">
                    {new Date(selectedReview.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Conversation */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Conversation</h3>
              <div className="border-2 border-[#1a1a2e] rounded-xl p-4 bg-gray-50 max-h-64 overflow-y-auto space-y-3">
                {messagesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-5 h-5 text-violet-600 animate-spin" />
                  </div>
                ) : conversationMessages.length === 0 ? (
                  <div className="text-center py-6">
                    <MessageSquare className="w-6 h-6 text-gray-300 mx-auto mb-2" />
                    <p className="text-xs text-gray-500">No messages found.</p>
                  </div>
                ) : (
                  conversationMessages.map((msg) => {
                    const isHuman = msg.sender_type === 'human' || msg.sender_type === 'agent';
                    return (
                      <div key={msg.id} className={cn('flex', isHuman ? 'justify-end' : 'justify-start')}>
                        <div className={cn(
                          'max-w-[75%] rounded-lg p-2.5',
                          isHuman ? 'bg-violet-500 text-white' : 'bg-white text-gray-900 border-2 border-[#1a1a2e]'
                        )}>
                          <span className={cn('text-xs font-semibold block mb-0.5', isHuman ? 'text-violet-100' : 'text-gray-500')}>
                            {isHuman ? 'Trainer' : 'Customer'}
                          </span>
                          <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Verdict Form */}
            <div className="space-y-4">
              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Verdict
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setVerdictStatus('approved')}
                    className={cn(
                      'flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all',
                      verdictStatus === 'approved'
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300'
                    )}
                  >
                    <span
                      className={cn(
                        'w-2 h-2 rounded-full',
                        verdictStatus === 'approved' ? 'bg-green-500' : 'bg-gray-400'
                      )}
                    />
                    <span
                      className={cn(
                        'text-sm font-medium',
                        verdictStatus === 'approved' ? 'text-green-700' : 'text-gray-700'
                      )}
                    >
                      Approved
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setVerdictStatus('rejected')}
                    className={cn(
                      'flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all',
                      verdictStatus === 'rejected'
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-200 hover:border-gray-300'
                    )}
                  >
                    <span
                      className={cn(
                        'w-2 h-2 rounded-full',
                        verdictStatus === 'rejected' ? 'bg-red-500' : 'bg-gray-400'
                      )}
                    />
                    <span
                      className={cn(
                        'text-sm font-medium',
                        verdictStatus === 'rejected' ? 'text-red-700' : 'text-gray-700'
                      )}
                    >
                      Rejected
                    </span>
                  </button>
                </div>
              </div>

              {/* Score */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Score
                </label>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setVerdictScore(star)}
                      className="p-1 hover:scale-110 transition-transform"
                    >
                      <Star
                        className={cn(
                          'w-7 h-7 transition-colors',
                          star <= verdictScore
                            ? 'text-yellow-400 fill-yellow-400'
                            : 'text-gray-300 hover:text-yellow-300'
                        )}
                      />
                    </button>
                  ))}
                  <span className="text-sm font-medium text-gray-600 ml-2">
                    {verdictScore}/5
                  </span>
                </div>
              </div>

              {/* Feedback */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Feedback
                </label>
                <textarea
                  value={verdictFeedback}
                  onChange={(e) => setVerdictFeedback(e.target.value)}
                  placeholder="Provide detailed feedback on the training session performance..."
                  rows={6}
                  className="w-full px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none text-sm resize-none"
                />
              </div>

              {/* Submit */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#1a1a2e]">
                <button
                  type="button"
                  onClick={closeReviewModal}
                  className="px-4 py-2.5 text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitVerdict}
                  disabled={isSubmitting}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-violet-500 text-white font-semibold rounded-xl hover:bg-violet-600 transition-colors shadow-[3px_3px_0px_#1a1a2e] disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Verdict'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingReviewsPage;
